import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import {
  fetchCampaignTypeConfigs,
  fetchSyncStatus,
  updateCampaignTypeConfigs,
  triggerCampaignSync,
  type CampaignTypeConfig,
} from '../api-client';
import { LAUNCHER_SYNC_STATUS_QUERY_KEY, type LauncherSyncStatus } from '../../_shared/launcher-campaign-member-schema';

const TYPES_KEY = ['launcher', 'campaign-type-config'] as const;
const SYNC_STATUS_KEY = LAUNCHER_SYNC_STATUS_QUERY_KEY;

export function useCampaignTypeConfigs() {
  const { apiFetch } = useApi();
  const query = useQuery<CampaignTypeConfig[]>({
    queryKey: TYPES_KEY,
    queryFn: () => fetchCampaignTypeConfigs(apiFetch),
    staleTime: Infinity,
  });
  return {
    configs: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  } as const;
}

export function useSyncStatus() {
  const { apiFetch } = useApi();
  const query = useQuery<LauncherSyncStatus>({
    queryKey: SYNC_STATUS_KEY,
    queryFn: () => fetchSyncStatus(apiFetch),
    staleTime: 0,
    refetchInterval: 60_000,
  });
  return {
    syncStatus: query.data ?? null,
    isLoading: query.isLoading,
  } as const;
}

export function useToggleCampaignType() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: { type: string; subtype: string | null; enabled: boolean }) =>
      updateCampaignTypeConfigs(apiFetch, [update]),
    onMutate: async ({ type, subtype, enabled }) => {
      await queryClient.cancelQueries({ queryKey: TYPES_KEY });
      const prev = queryClient.getQueryData<CampaignTypeConfig[]>(TYPES_KEY);
      if (prev) {
        queryClient.setQueryData(TYPES_KEY, prev.map(c =>
          c.type === type && c.subtype === subtype ? { ...c, enabled } : c,
        ));
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(TYPES_KEY, ctx.prev);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns'] });
    },
  });
}

export function useTriggerCampaignSync() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => triggerCampaignSync(apiFetch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TYPES_KEY });
      void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
      // The sync rewrites the campaign list; it is cached indefinitely, so refetch it here.
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns'] });
    },
  });
}
